import { Grid, InputLabel, ListItem, makeStyles, MenuItem, Select } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useGetAvailableDaysQuery } from "../../../api/rtk/ExamSessionApi";
import { Language, LanguageLabels, SwissGermanLocale, SwissTimeZone } from "../../../data/constants";
import { ExamLocationViewModel, ExamLocationDayViewModel } from "../../../model/ExamSignup/ModuleExamDetailsViewModel";
import { GetTranslation } from "../../../translations/GetTranslation";
import CustomDraggableDialog from "../../common/customDialog/CustomDraggableDialog";
import FormGridCell from "../../common/FormGridCell";
import TranslatedMessage from "../../translations/TranslatedMessage";
import { ExamType } from "../../../model/Exam";
import LoadingIndicator from "../../common/LoadingIndicator";

interface ExamSessionCandidateExamBookDialogProps {
  opened: boolean;
  examSessionId: string;
  examId: string;
  examType: ExamType;
  language: Language;
  locationId?: string;
  examDayId?: string;
  accept: (locationId: string, examDayId: string, language: Language) => void;
  decline: () => void;
}

const useStyles = makeStyles({
  select: {
    width: "100%",
  },
});

const ExamSessionCandidateExamBookDialog: React.FC<ExamSessionCandidateExamBookDialogProps> = ({
  opened,
  examSessionId,
  examId,
  examType,
  language,
  locationId,
  examDayId,
  accept,
  decline,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const [selectedLocation, setSelectedLocation] = useState<ExamLocationViewModel | undefined>(undefined);
  const [selectedDay, setSelectedDay] = useState<ExamLocationDayViewModel | undefined>(undefined);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(language);
  const { data: locationViewModels, isFetching, refetch } = useGetAvailableDaysQuery({ examSessionId, examId,language: selectedLanguage });

  useEffect(() => {
    const locationViewModel = locationViewModels?.find((location) => location.locationId === locationId);
    setSelectedLocation(locationViewModel);
  }, [locationViewModels, locationId]);

  useEffect(() => {
    const dayViewModel = selectedLocation?.examDays.find((day) => day.examDayId === examDayId);
    setSelectedDay(dayViewModel);
  }, [selectedLocation, examDayId]);

  const handleExamDayChanged = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selectedDayId = event.target.value as string;
    const dayViewModel = selectedLocation?.examDays.find((day) => day.examDayId === selectedDayId);
    setSelectedDay(dayViewModel);
  };

  const handleLocationChanged = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selectedLocationId = event.target.value as string;
    const locationViewModel = locationViewModels?.find((location) => location.locationId === selectedLocationId);
    setSelectedLocation(locationViewModel);
    const dayViewModel = locationViewModel?.examDays[0];
    setSelectedDay(dayViewModel);
  };

  const handleUpdateAvailableExamDay = async () => {
    if (selectedLocation === undefined || selectedDay === undefined) {
      return;
    }
    accept(selectedLocation.locationId, selectedDay.examDayId, selectedLanguage);
  };

  const handleLanguageChanged = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    setSelectedLanguage(event.target.value as Language);
    refetch();
  };

  const examTypeLabel =
    examType === ExamType.Oral
      ? GetTranslation(intl, "exam.type.oral")
      : GetTranslation(intl, "exam.type.written");
  const locationAndDate = GetTranslation(intl, "examSessionCandidate.bookingDialog.title");

  return (
    <CustomDraggableDialog
      acceptText={<TranslatedMessage id="common.accept" />}
      cancelText={<TranslatedMessage id="common.cancel" />}
      content={
        <>
          <ListItem>
            <Grid container={true} spacing={2}>
            <FormGridCell size="wide">
                <>
                  <InputLabel>
                    <TranslatedMessage id="examSessionCandidate.bookingDialog.selectLanguage" />
                  </InputLabel>
                        <Select
                          value={selectedLanguage}
                          className={classes.select}
                          onChange={handleLanguageChanged}
                        >
                          {Object.keys(Language).map((languageKey) => {
                            const lang = Language[languageKey as Language];
                            return (
                              <MenuItem key={lang} value={lang} >
                                {LanguageLabels[lang]}
                              </MenuItem>
                            );
                          })}
                        </Select>
                </>
              </FormGridCell>
              <FormGridCell size="wide">
                <>
                  <InputLabel>
                    <TranslatedMessage id="examSessionCandidate.bookingDialog.selectLocation" />
                  </InputLabel>
                  <Select
                    value={selectedLocation?.locationId || ""}
                    onChange={handleLocationChanged}
                    className={classes.select}
                  >
                    {locationViewModels?.map((location) => (
                      <MenuItem key={location.locationId} value={location.locationId}>
                        {location.name}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              </FormGridCell>
              <FormGridCell size="wide">
                <>
                  <InputLabel>
                    <TranslatedMessage id="examSessionCandidate.bookingDialog.selectDate" />
                  </InputLabel>
                  <Select
                    value={selectedDay?.examDayId || ""}
                    onChange={handleExamDayChanged}
                    className={classes.select}
                  >
                    {selectedLocation?.examDays.map((day) => (
                      <MenuItem key={day.examDayId} value={day.examDayId}>
                        {new Date(day.date).toLocaleDateString(SwissGermanLocale, { timeZone: SwissTimeZone })}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              </FormGridCell>
            </Grid>
          </ListItem>
          <LoadingIndicator show={isFetching} />
        </>
      }
      isOpen={opened}
      onAccept={handleUpdateAvailableExamDay}
      onCancel={decline}
      title={locationAndDate + " - " + examTypeLabel}
    />
  );
};

export default ExamSessionCandidateExamBookDialog;
